const vertexSource = `attribute vec2 a_position;
varying vec2 v_uv;
void main() { v_uv = a_position * .5 + .5; gl_Position = vec4(a_position, 0., 1.); }`;

const fragmentSource = `precision highp float;
varying vec2 v_uv;
uniform sampler2D u_scene;
uniform vec2 u_size;
uniform vec2 u_center;
uniform vec2 u_radius;
uniform float u_visible;
uniform float u_activation;
vec4 scene(vec2 p) { return texture2D(u_scene, p / u_size); }
void main() {
  vec2 p = vec2(v_uv.x, 1. - v_uv.y) * u_size;
  vec2 delta = p - u_center;
  float corner = u_radius.y * mix(1., .92, u_activation);
  vec2 local = abs(delta) - (u_radius - corner);
  float d = length(max(local, 0.)) + min(max(local.x, local.y), 0.) - corner;
  float mask = (1. - smoothstep(-.7, .7, d)) * u_visible;
  vec2 normal = normalize(sign(delta) * max(local, 0.) + vec2(.00001));
  float edge = smoothstep(-corner * .5, -.4, d);
  // Toward the rim the glass bends harder: content bulges outward and splits into colour fringes.
  float bend = pow(edge, 1.6) * u_activation;
  vec2 warped = u_center + delta * mix(1., .82, u_activation) * (1. - bend * .5) + normal * edge * 6. * u_activation;
  float dispersion = bend * 3.;
  vec4 middle = scene(warped);
  vec3 glass = vec3(scene(warped + normal * dispersion).r, middle.g, scene(warped - normal * dispersion).b);
  // Keep the colour split as a faint fringe rather than a yellow/blue cast.
  glass = mix(vec3(dot(glass, vec3(.2126, .7152, .0722))), glass, mix(1., .45, bend));
  float light = clamp(dot(normal, normalize(vec2(-.4, -.9))) * .5 + .5, 0., 1.);
  float reflection = pow(edge, 3.) * pow(light, 3.);
  glass *= 1. - edge * edge * .07 * u_activation;
  float surface = smoothstep(.15, .7, dot(glass, vec3(.2126, .7152, .0722)));
  glass = mix(glass, vec3(.88), surface * .72 * (1. - u_activation));
  // The body is only as opaque as the scene it refracts, so the lens stays clear where it overhangs the bar;
  // the highlight and a hairline rim sit on top (premultiplied).
  vec4 lens = vec4(glass * middle.a, middle.a);
  lens = mix(lens, vec4(1.), reflection * .55 * u_activation);
  float rim = exp(-pow((d + .7) / .6, 2.)) * .75 * u_activation;
  lens = mix(lens, vec4(mix(vec3(.74), vec3(1.), light), 1.), rim);
  float shadow = exp(-pow((d - 1.5) / 2.5, 2.)) * .03 * u_activation * smoothstep(-.4, .7, delta.y / u_radius.y) * u_visible;
  gl_FragColor = lens * mask + vec4(0., 0., 0., shadow * (1. - mask));
}`;

// Canvas overhang around the bar, so the lens can swell and stretch past the bar's edges.
const padX = 44;
const padY = 26;

function createRenderer(canvas: HTMLCanvasElement) {
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
  if (!gl) return null;
  function shader(type: number, code: string) {
    const result = gl!.createShader(type);
    if (!result) return null;
    gl!.shaderSource(result, code);
    gl!.compileShader(result);
    if (gl!.getShaderParameter(result, gl!.COMPILE_STATUS)) return result;
    gl!.deleteShader(result);
    return null;
  }
  const vertex = shader(gl.VERTEX_SHADER, vertexSource);
  const fragment = shader(gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { gl.deleteProgram(program); return null; }
  gl.useProgram(program);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  const attribute = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(attribute);
  gl.vertexAttribPointer(attribute, 2, gl.FLOAT, false, 0, 0);
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const uniforms = Object.fromEntries(['size', 'center', 'radius', 'visible', 'activation'].map(name => [name, gl.getUniformLocation(program, `u_${name}`)]));
  gl.uniform1i(gl.getUniformLocation(program, 'u_scene'), 0);
  return {
    upload(source: HTMLCanvasElement) {
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    },
    draw(width: number, height: number, x: number, rx: number, ry: number, visible: boolean, activation: number) {
      gl.uniform2f(uniforms.size, width, height);
      gl.uniform2f(uniforms.center, x, height / 2);
      gl.uniform2f(uniforms.radius, rx, ry);
      gl.uniform1f(uniforms.visible, visible ? 1 : 0);
      gl.uniform1f(uniforms.activation, activation);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    },
    dispose() { gl.deleteTexture(texture); gl.deleteBuffer(buffer); gl.deleteProgram(program); },
  };
}

// Damped spring step; returns the new [value, velocity].
function spring(value: number, velocity: number, target: number, stiffness: number, damping: number, dt: number) {
  velocity += ((target - value) * stiffness - velocity * damping) * dt;
  return [value + velocity * dt, velocity] as const;
}

export function mountLiquidLens(nav: HTMLElement, canvas: HTMLCanvasElement) {
  const links = [...nav.querySelectorAll<HTMLAnchorElement>('a')];
  const source = document.createElement('canvas');
  const context = source.getContext('2d')!;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let renderer = createRenderer(canvas);
  let width = 0;
  let height = 0;
  let centers: number[] = [];
  let position = 0;
  let target = 0;
  let velocity = 0;
  // Lens speed (0..1, may undershoot while wobbling), bar press (0..1) and edge pull (px) all ride on springs.
  let speed = 0;
  let speedVelocity = 0;
  let press = 0;
  let pressVelocity = 0;
  let pull = 0;
  let pullVelocity = 0;
  let pullTarget = 0;
  let activation = 0;
  let activeUntil = 0;
  let selectedIndex = -1;
  let frame = 0;
  let previousTime = 0;
  let visible = false;
  let initialized = false;
  let disposed = false;
  let pointer: { id: number; x: number; y: number; moved: boolean; touchId?: number } | null = null;
  let suppressClick = false;
  nav.dataset.glassRenderer = renderer ? 'webgl' : 'fallback';
  nav.style.setProperty('--lens-pad-x', `${padX}px`);
  nav.style.setProperty('--lens-pad-y', `${padY}px`);

  // Client coordinates → unscaled canvas coordinates, independent of the bar's current deformation.
  function toCanvas(clientX: number, clientY: number) {
    const bounds = nav.getBoundingClientRect();
    const scaleX = bounds.width / nav.offsetWidth || 1;
    const scaleY = bounds.height / nav.offsetHeight || 1;
    return [(clientX - bounds.left) / scaleX - nav.clientLeft + padX, (clientY - bounds.top) / scaleY - nav.clientTop + padY];
  }

  function paint() {
    const scale = source.width / width;
    const selectedColor = getComputedStyle(nav).getPropertyValue('--nav-selected').trim() || '#25292d';
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.clearRect(0, 0, width, height);
    // Opaque enough over the bar to cover the DOM icons beneath; nearly clear where the lens overhangs the page.
    const corner = parseFloat(getComputedStyle(nav).borderTopLeftRadius) || 0;
    context.fillStyle = 'rgba(250,251,253,.87)';
    context.beginPath();
    if (context.roundRect) context.roundRect(padX - nav.clientLeft, padY - nav.clientTop, nav.offsetWidth, nav.offsetHeight, corner);
    else context.rect(padX - nav.clientLeft, padY - nav.clientTop, nav.offsetWidth, nav.offsetHeight);
    context.fill();
    context.globalCompositeOperation = 'destination-over';
    context.fillStyle = 'rgba(255,255,255,.1)';
    context.fillRect(0, 0, width, height);
    context.globalCompositeOperation = 'source-over';
    // The scene is only visible through the lens, so every icon is drawn in its selected style.
    centers = links.map(link => {
      const box = link.getBoundingClientRect();
      const x = toCanvas(box.left + box.width / 2, 0)[0];
      for (const element of link.querySelectorAll<HTMLElement>('i, span')) {
        const style = getComputedStyle(element);
        if (style.display === 'none') continue;
        const text = element.tagName === 'I' ? getComputedStyle(element, '::before').content.match(/^["']([^"']*)["']/)?.[1] || '' : element.textContent || '';
        const rectangle = element.getBoundingClientRect();
        context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        context.fillStyle = selectedColor;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(text, x, toCanvas(0, rectangle.top + rectangle.height / 2)[1]);
      }
      return x;
    });
    renderer?.upload(source);
  }

  function render() {
    const barHeight = height - padY * 2;
    const barWidth = width - padX * 2;
    const radius = Math.max(barHeight * .5, barHeight * (.70 + .10 * activation) + speed * barHeight * .34);
    const verticalRadius = Math.max(barHeight * .36, barHeight * (.43 + .19 * activation) - speed * barHeight * .08);
    nav.dataset.lensVisible = String(visible);
    nav.dataset.glassState = activation > .5 ? 'expanded' : 'resting';
    const stretch = Math.abs(pull) / barWidth;
    nav.style.setProperty('--bar-shift', `${(pull * .32).toFixed(2)}px`);
    nav.style.setProperty('--bar-scale-x', (1 + press * .028 + speed * .01 + stretch * .5).toFixed(4));
    nav.style.setProperty('--bar-scale-y', (1 + press * .05 - speed * .018 - stretch * .25).toFixed(4));
    if (renderer) renderer.draw(width, height, position, radius, verticalRadius, visible, activation);
    else {
      nav.style.setProperty('--lens-x', `${position - radius - padX}px`);
      nav.style.setProperty('--lens-width', `${radius * 2}px`);
      nav.style.setProperty('--lens-height', `${verticalRadius * 2}px`);
      nav.style.setProperty('--lens-corner', `${verticalRadius * (1 - activation * .08)}px`);
      nav.style.setProperty('--lens-tint', `rgba(225,225,225,${.66 - activation * .56})`);
    }
  }

  function settle() {
    position = target; velocity = speed = speedVelocity = press = pressVelocity = pull = pullVelocity = 0;
  }

  function animate(time: number) {
    const dt = Math.min((time - previousTime) / 1000 || 1 / 60, .032);
    previousTime = time;
    [position, velocity] = spring(position, velocity, target, 680, 48, dt);
    [speed, speedVelocity] = spring(speed, speedVelocity, Math.min(1, Math.abs(velocity) / 1300), 260, 15, dt);
    const pressed = pointer !== null || time < activeUntil;
    [press, pressVelocity] = spring(press, pressVelocity, pressed ? 1 : 0, 420, 24, dt);
    [pull, pullVelocity] = spring(pull, pullVelocity, pointer?.moved ? pullTarget : 0, 520, 30, dt);
    const expanded = pressed || Math.abs(target - position) > 1 || Math.abs(velocity) > 18;
    const nextActivation = expanded ? 1 : 0;
    activation += (nextActivation - activation) * Math.min(1, dt * 16);
    render();
    const moving = Math.abs(target - position) > .03 || Math.abs(velocity) > .4;
    const deforming = Math.abs(speed) + Math.abs(speedVelocity) > .002 || Math.abs(press - (pressed ? 1 : 0)) + Math.abs(pressVelocity) > .002 || Math.abs(pull) + Math.abs(pullVelocity) > .05;
    if (moving || deforming || pressed || Math.abs(nextActivation - activation) > .002) frame = requestAnimationFrame(animate);
    else { frame = 0; settle(); activation = nextActivation; render(); }
  }

  function wake() {
    if (!frame && !document.hidden && !motion.matches) { previousTime = performance.now(); frame = requestAnimationFrame(animate); }
  }

  function move(next: number, snap = false) {
    target = next;
    if (snap || motion.matches) {
      cancelAnimationFrame(frame); frame = 0; settle(); activation = pointer ? 1 : 0; render();
    } else wake();
  }

  function sync(snap = false) {
    paint();
    if (pointer?.moved) return;
    const index = links.findIndex(link => link.getAttribute('aria-current') === 'page');
    if (initialized && !snap && index >= 0 && index !== selectedIndex) activeUntil = performance.now() + 220;
    selectedIndex = index;
    visible = index >= 0;
    if (visible) move(centers[index], snap || !initialized);
    else render();
    initialized = true;
  }

  function resize() {
    width = nav.clientWidth + padX * 2;
    height = nav.clientHeight + padY * 2;
    const scale = Math.min(devicePixelRatio || 1, 2);
    canvas.width = source.width = Math.round(width * scale);
    canvas.height = source.height = Math.round(height * scale);
    sync(true);
  }

  function begin(id: number, x: number, y: number, target: Element | null, touchId?: number) {
    const link = target?.closest('a');
    if (pointer || !link) return;
    pointer = { id, x, y, moved: false, touchId };
    suppressClick = false;
    activeUntil = performance.now() + 220;
    visible = true;
    wake();
  }

  function advance(id: number, x: number, y: number, capture = false) {
    if (id !== pointer?.id) return false;
    const dx = Math.abs(x - pointer.x);
    const dy = Math.abs(y - pointer.y);
    if (!pointer.moved) {
      if (dy > dx && dy > 5) { pointer = null; activeUntil = 0; sync(); return; }
      if (dx < 5) return;
      pointer.moved = true;
      if (capture) nav.setPointerCapture(id);
    }
    visible = true;
    const raw = toCanvas(x, y)[0];
    const clamped = Math.max(centers[0], Math.min(centers[links.length - 1], raw));
    const overflow = raw - clamped;
    // Rubber band past the first/last item: the lens trails the finger and drags the bar along with it.
    pullTarget = Math.sign(overflow) * 34 * (1 - Math.exp(-Math.abs(overflow) / 80));
    move(clamped + pullTarget);
    return true;
  }

  function finish(id: number) {
    if (id !== pointer?.id) return;
    const dragged = pointer.moved;
    pointer = null;
    activeUntil = performance.now() + 160;
    if (!dragged) { sync(); return; }
    suppressClick = true;
    const resting = target - pullTarget;
    pullTarget = 0;
    const nearest = centers.reduce((best, x, index) => Math.abs(x - resting) < Math.abs(centers[best] - resting) ? index : best, 0);
    move(centers[nearest]);
    links[nearest].click();
  }

  function pointerDown(event: PointerEvent) {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    begin(event.pointerId, event.clientX, event.clientY, event.target as Element);
  }
  function pointerMove(event: PointerEvent) {
    if (pointer?.touchId === undefined) advance(event.pointerId, event.clientX, event.clientY, true);
  }
  function pointerUp(event: PointerEvent) { finish(event.pointerId); }
  function touchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return;
    const touch = event.changedTouches[0];
    if (touch) begin(-touch.identifier - 1, touch.clientX, touch.clientY, document.elementFromPoint(touch.clientX, touch.clientY), touch.identifier);
  }
  function touchMove(event: TouchEvent) {
    if (pointer?.touchId === undefined) return;
    const touch = [...event.changedTouches].find(item => item.identifier === pointer?.touchId);
    if (touch && advance(-touch.identifier - 1, touch.clientX, touch.clientY)) event.preventDefault();
  }
  function touchEnd(event: TouchEvent) {
    const touch = [...event.changedTouches].find(item => item.identifier === pointer?.touchId);
    if (touch) finish(-touch.identifier - 1);
  }

  function pointerCancel(event: PointerEvent) {
    if (!pointer || pointer.touchId !== undefined || event.pointerId !== pointer.id) return;
    clearPointer();
  }
  function touchCancel() {
    if (!pointer) return;
    clearPointer();
  }
  function clearPointer() {
    pointer = null; activeUntil = 0; pullTarget = 0; sync();
  }
  function captureClick(event: MouseEvent) {
    if (suppressClick && event.isTrusted) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
  }
  function visibilityChange() {
    if (document.hidden) { pointer = null; activeUntil = activation = pullTarget = 0; cancelAnimationFrame(frame); frame = 0; settle(); render(); }
    else sync(true);
  }
  function motionChange() { pointer = null; activeUntil = pullTarget = 0; sync(true); }
  function contextLost(event: Event) {
    event.preventDefault(); renderer = null; nav.dataset.glassRenderer = 'fallback'; render();
  }

  const observer = new MutationObserver(() => sync());
  links.forEach(link => observer.observe(link, { attributes: true, attributeFilter: ['class', 'aria-current'] }));
  const sizeObserver = new ResizeObserver(resize);
  sizeObserver.observe(nav);
  nav.addEventListener('pointerdown', pointerDown);
  nav.addEventListener('pointermove', pointerMove);
  nav.addEventListener('pointerup', pointerUp);
  nav.addEventListener('pointercancel', pointerCancel);
  nav.addEventListener('lostpointercapture', pointerCancel);
  nav.addEventListener('touchstart', touchStart, { passive: true });
  nav.addEventListener('touchmove', touchMove, { passive: false });
  nav.addEventListener('touchend', touchEnd);
  nav.addEventListener('touchcancel', touchCancel);
  nav.addEventListener('click', captureClick, true);
  canvas.addEventListener('webglcontextlost', contextLost);
  document.addEventListener('visibilitychange', visibilityChange);
  motion.addEventListener('change', motionChange);
  void document.fonts.ready.then(() => { if (!disposed) sync(true); });
  resize();
  return () => {
    disposed = true;
    cancelAnimationFrame(frame);
    observer.disconnect(); sizeObserver.disconnect();
    nav.removeEventListener('pointerdown', pointerDown);
    nav.removeEventListener('pointermove', pointerMove);
    nav.removeEventListener('pointerup', pointerUp);
    nav.removeEventListener('pointercancel', pointerCancel);
    nav.removeEventListener('lostpointercapture', pointerCancel);
    nav.removeEventListener('touchstart', touchStart);
    nav.removeEventListener('touchmove', touchMove);
    nav.removeEventListener('touchend', touchEnd);
    nav.removeEventListener('touchcancel', touchCancel);
    nav.removeEventListener('click', captureClick, true);
    canvas.removeEventListener('webglcontextlost', contextLost);
    document.removeEventListener('visibilitychange', visibilityChange);
    motion.removeEventListener('change', motionChange);
    renderer?.dispose();
  };
}
